import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useDashboard } from "./hooks/useDashboard";
import ViewingView from "./components/ViewingView";
import { useDeviceStore } from "../../store/useDeviceStore";
import ReportDialogModal from "../../components/modal/ReportDialogModal";
import useMqttStore from "../../store/useMqttStore";
import { brokerUrl, topicPrefix } from "../../constant";
import { IWeatherData } from "../../types/device";
import LocationSelection from "../new/LocationSelection";
import DeviceSelection from "../new/DeviceSelection";
import DeviceStatusBar from "../../components/layout/DeviceStatusBar";
import { useGetMultiDeviceParameters } from "../../queries/parameters";
import { useGetAllLocations, useGetDevicesForLocation } from "../../queries/admin";
import { useLocationStore } from "../../store/useLocationStore";
import { useValueStore } from "../../store/useValueState";
import { CheckIcon, InboxIcon } from "lucide-react";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
export default function DashboardPage() {
  const { loadDashboard, activeWidgets, content, isLoading: isDashboardLoading } = useDashboard();
  const { selectedDeviceId, selectedDeviceIds } = useDeviceStore();
  const { selectedLocationId } = useLocationStore();
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [navSlot, setNavSlot] = useState<HTMLElement | null>(null);
  const [filterSlot, setFilterSlot] = useState<HTMLElement | null>(null);

  // Fetch locations & devices to detect empty state
  const { data: allLocations, isLoading: isLocationsLoading } = useGetAllLocations();
  const { data: locationDevices, isLoading: isDevicesLoading } = useGetDevicesForLocation(
    selectedLocationId && selectedLocationId !== "not-available" ? selectedLocationId : null,
  );
  const hasNoLocations = !isLocationsLoading && (!allLocations || allLocations.length === 0);
  const hasNoDevices =
    !isDevicesLoading && !hasNoLocations && (!locationDevices || locationDevices.length === 0);
  // Fetch parameters across every selected device so the filter dropdown shows
  // the union of identifiers (deduped). With a single device, this behaves
  // identically to the single-device query.
  const paramFetchIds =
    selectedDeviceIds.length > 0 ? selectedDeviceIds : selectedDeviceId ? [selectedDeviceId] : [];
  const { data: deviceParameters, isLoading: isParamsLoading } =
    useGetMultiDeviceParameters(paramFetchIds);
  const { setValue } = useValueStore();

  // Multi-select parameter filter state
  const [selectedParamIds, setSelectedParamIds] = useState<Set<string>>(new Set());
  const [isParamDropdownOpen, setIsParamDropdownOpen] = useState(false);
  const paramDropdownRef = useRef<HTMLDivElement>(null);
  const selectionInitDeviceRef = useRef<string | null>(null);

  // Portal: inject content into the layout navbar
  useEffect(() => {
    setNavSlot(document.getElementById("navbar-slot"));
    setFilterSlot(document.getElementById("navbar-filters-slot"));
  }, []);

  const parameterOptions = (deviceParameters || []).map((p: any) => ({
    id: p.id || p.identifier,
    name: p.name,
    identifier: p.identifier,
  }));

  // Default: all parameters selected whenever the selected device set changes
  // (adding a device should expose its new parameters, removing should drop them).
  const deviceSetKey = selectedDeviceIds.join(",") || selectedDeviceId || "";
  useEffect(() => {
    if (parameterOptions.length > 0 && selectionInitDeviceRef.current !== deviceSetKey) {
      selectionInitDeviceRef.current = deviceSetKey;
      setSelectedParamIds(new Set(parameterOptions.map((p: any) => p.id)));
      setValue({ metrics: parameterOptions.map((p: any) => p.name) });
    }
  }, [deviceParameters, deviceSetKey]);

  // Close param dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (paramDropdownRef.current && !paramDropdownRef.current.contains(event.target as Node)) {
        setIsParamDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleParam = (paramId: string) => {
    const next = new Set(selectedParamIds);
    if (next.has(paramId)) {
      if (next.size <= 1) return;
      next.delete(paramId);
    } else {
      next.add(paramId);
    }
    setSelectedParamIds(next);
    const selectedNames = parameterOptions
      .filter((p: any) => next.has(p.id))
      .map((p: any) => p.name);
    setValue({ metrics: selectedNames });
  };

  const toggleAllParams = () => {
    const allSelected = selectedParamIds.size === parameterOptions.length;
    if (allSelected) {
      // Select only the first one
      const first = parameterOptions[0];
      setSelectedParamIds(new Set([first.id]));
      setValue({ metrics: [first.name] });
    } else {
      // Select all
      setSelectedParamIds(new Set(parameterOptions.map((p: any) => p.id)));
      setValue({ metrics: parameterOptions.map((p: any) => p.name) });
    }
  };

  const getParamButtonLabel = () => {
    if (selectedParamIds.size === 0) return "None";
    if (selectedParamIds.size === parameterOptions.length) return "All";
    if (selectedParamIds.size === 1) {
      const selected = parameterOptions.find((p: any) => selectedParamIds.has(p.id));
      return selected?.name || "";
    }
    return `${selectedParamIds.size} selected`;
  };

  // Get selected parameter identifiers for filtering widgets
  const selectedParameterIdentifiers = parameterOptions
    .filter((p: any) => selectedParamIds.has(p.id))
    .map((p: any) => p.identifier);

  // MQTT integration
  const {
    connectMqtt,
    disconnectMqtt,
    subscribeTopic,
    unsubscribeTopic,
    deviceDataMap,
    isConnected,
  } = useMqttStore();
  const [liveData, setLiveData] = useState<IWeatherData | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  // Force re-render every 30 seconds so "Updated X mins ago" stays current
  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(interval);
  }, []);

  // MQTT connection
  useEffect(() => {
    connectMqtt(brokerUrl);
    return () => {
      disconnectMqtt();
    };
  }, [connectMqtt, disconnectMqtt]);

  // Subscribe to all selected device topics
  useEffect(() => {
    if (selectedDeviceIds.length > 0 && isConnected) {
      const topics = selectedDeviceIds.map((id) => `${topicPrefix}/${id}`);
      subscribeTopic(topics);
      return () => {
        topics.forEach((topic) => unsubscribeTopic(topic));
      };
    }
  }, [selectedDeviceIds, isConnected, subscribeTopic, unsubscribeTopic]);

  // Update live data when MQTT data changes (use primary device for header status)
  useEffect(() => {
    if (selectedDeviceId && deviceDataMap[selectedDeviceId]) {
      const deviceData = deviceDataMap[selectedDeviceId];
      setLiveData(deviceData.data);
      setLastUpdated(deviceData.lastUpdated);
    } else {
      setLiveData(null);
      setLastUpdated(null);
    }
  }, [deviceDataMap, selectedDeviceId]);

  // Helper function to format the last updated time
  const getTimeAgo = (isoString: string) => {
    const now = new Date();
    const updated = new Date(isoString);
    const diffMs = now.getTime() - updated.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSecs < 60) return "Just now";
    if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? "s" : ""} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
    return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
  };

  useEffect(() => {
    if (selectedDeviceId) {
      loadDashboard(selectedDeviceId);
    } else {
      loadDashboard("default");
    }
  }, [selectedDeviceId, loadDashboard]);

  const isLoading = isDashboardLoading || isParamsLoading;

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white dark:bg-gray-900">
        <div className="relative">
          <div className="h-16 w-16 rounded-full border-4 border-gray-200 dark:border-gray-700" />
          <div className="absolute top-0 h-16 w-16 animate-spin rounded-full border-4 border-transparent border-t-primary" />
        </div>
        <p className="mt-4 text-sm font-medium text-gray-600 dark:text-gray-400">
          Loading dashboard...
        </p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Portal: inject title, description, and device status into the layout navbar */}
      {navSlot &&
        createPortal(
          <div className="min-w-0 flex-1 hidden md:flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white truncate">
                {content.pageTitle}
              </h1>
              <p className="text-gray-600 dark:text-gray-400 truncate mt-1">
                {content.description}
              </p>
            </div>
            <DeviceStatusBar />
          </div>,
          navSlot,
        )}

      {/* Filter Bar: Location / Device / Parameter — portaled into the sticky header */}
      {filterSlot &&
        createPortal(
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <LocationSelection />
            <span className="mt-5 text-gray-300 dark:text-gray-600 text-3xl font-medium">/</span>
            <DeviceSelection />
            <span className="mt-5 text-gray-300 dark:text-gray-600 text-3xl font-medium">/</span>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400">
                Parameter
              </label>
              {parameterOptions.length > 0 ? (
              <div className="relative w-64" ref={paramDropdownRef}>
                <button
                  onClick={() => setIsParamDropdownOpen(!isParamDropdownOpen)}
                  className="h-10 cursor-pointer rounded-lg bg-white dark:bg-gray-800 py-1.5 pl-3 pr-8 text-left text-gray-900 dark:text-gray-200 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:outline-none focus:ring-2 focus:ring-primary text-sm sm:leading-6 w-64"
                >
                  <span className="block truncate font-medium text-sm">
                    {getParamButtonLabel()}
                  </span>
                  <span className="absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  </span>
                </button>

                {isParamDropdownOpen && (
                  <ul className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-[#1F2C33] py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-600 dark:border dark:border-gray-600 focus:outline-none sm:text-sm">
                    <li
                      onClick={toggleAllParams}
                      className="relative cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 select-none py-2 px-3 text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-600 font-medium"
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${
                            selectedParamIds.size === parameterOptions.length
                              ? "bg-primary border-primary"
                              : "border-gray-300 dark:border-gray-500"
                          }`}
                        >
                          {selectedParamIds.size === parameterOptions.length && (
                            <CheckIcon className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <span className="text-sm">All Parameters</span>
                      </div>
                    </li>
                    {parameterOptions.map((param: any, index: number) => {
                      const isChecked = selectedParamIds.has(param.id);
                      return (
                        <li
                          key={param.id}
                          onClick={() => toggleParam(param.id)}
                          className={`relative cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 select-none py-2 px-3 text-gray-900 dark:text-white ${
                            isChecked ? "bg-gray-50/50 dark:bg-gray-700/50" : ""
                          } ${
                            index !== parameterOptions.length - 1
                              ? "border-b border-gray-100 dark:border-gray-700"
                              : ""
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <div
                              className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${
                                isChecked
                                  ? "bg-primary border-primary"
                                  : "border-gray-300 dark:border-gray-500"
                              }`}
                            >
                              {isChecked && <CheckIcon className="w-3 h-3 text-white" />}
                            </div>
                            <span className="text-sm">{param.name}</span>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              ) : (
                <div className="w-64 h-10 px-3 py-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-500 dark:text-gray-400 text-sm flex items-center">
                  No parameters
                </div>
              )}
            </div>
          </div>,
          filterSlot,
        )}

      {/* Empty state: no locations or no devices — prompt the user to contact an admin */}
      {hasNoLocations || hasNoDevices ? (
        <div className="mt-16 flex flex-col items-center justify-center text-center px-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
            <InboxIcon className="h-8 w-8 text-gray-400 dark:text-gray-500" aria-hidden="true" />
          </div>
          <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
            {hasNoLocations ? "No locations available" : "No devices available"}
          </h3>
          <p className="mt-1 max-w-md text-sm text-gray-600 dark:text-gray-400">
            There {hasNoLocations ? "are no locations" : "are no devices for this location"} set up
            yet. Please contact your administrator to get access.
          </p>
        </div>
      ) : (
        <>
          {/* Dashboard Content - Always Live View */}
          <div className="relative">
            <ViewingView />
          </div>
        </>
      )}

      {/* Report Dialog Modal */}
      <ReportDialogModal isOpen={isReportModalOpen} onClose={() => setIsReportModalOpen(false)} />
    </div>
  );
}
